import { gql } from "@apollo/client/core";
import type { TypedDocumentNode } from "@graphql-typed-document-node/core";

import type { User } from "../types";

// ── Variable / Result types ───────────────────────────────────────────────────

export interface LoginMutationVariables {
  input: {
    email: string
    password: string
  }
}

export interface LoginMutationResult {
  login: {
    accessToken: string
  }
}

export interface GetMeQueryResult {
  me: User
}

export interface RequestPasswordResetMutationVariables {
  input: {
    email: string
  }
}

export interface RequestPasswordResetMutationResult {
  requestPasswordReset: boolean
}

export interface CheckUserTokenQueryVariables {
  token: string

  [key: string]: unknown
}

export interface CheckUserTokenQueryResult {
  checkUserToken: {
    isValid: boolean
  }
}

// ── Documents ─────────────────────────────────────────────────────────────────

export const LOGIN_MUTATION: TypedDocumentNode<LoginMutationResult, LoginMutationVariables> = gql`
    mutation Login($input: LoginInput!) {
        login(input: $input) {
            accessToken
        }
    }
`;

export const GET_ME_QUERY: TypedDocumentNode<GetMeQueryResult, Record<string, never>> = gql`
    query Me {
        me {
            id
            email
            name
            role
            orgId
            orgPermissions {
                orgId
                orgName
                permissions
            }
            createdAt
            updatedAt
        }
    }
`;

export const REQUEST_PASSWORD_RESET_MUTATION: TypedDocumentNode<
  RequestPasswordResetMutationResult,
  RequestPasswordResetMutationVariables
> = gql`
    mutation RequestPasswordReset($input: RequestPasswordResetInput!) {
        requestPasswordReset(input: $input)
    }
`;

export const CHECK_USER_TOKEN_QUERY: TypedDocumentNode<
  CheckUserTokenQueryResult,
  CheckUserTokenQueryVariables
> = gql`
    query CheckUserToken($token: String!) {
        checkUserToken(token: $token) {
            isValid
        }
    }
`;

export interface ConfirmPasswordResetMutationVariables {
  input: {
    newPassword: string
    token: string
  }
}

export interface ConfirmPasswordResetMutationResult {
  confirmPasswordReset: boolean
}

export const CONFIRM_PASSWORD_RESET_MUTATION: TypedDocumentNode<
  ConfirmPasswordResetMutationResult,
  ConfirmPasswordResetMutationVariables
> = gql`
    mutation ConfirmPasswordReset($input: ConfirmPasswordResetInput!) {
        confirmPasswordReset(input: $input)
    }
`;

export const LOGOUT_MUTATION: TypedDocumentNode<
  { logout: boolean },
  Record<string, never>
> = gql`
    mutation Logout {
        logout
    }
`;

export interface ResolveInviteTokenQueryVariables {
  token: string

  [key: string]: unknown
}

export interface ResolveInviteTokenQueryResult {
  resolveInviteToken: {
    isNewUser: boolean
    orgName: string
  }
}

export const RESOLVE_INVITE_TOKEN_QUERY: TypedDocumentNode<
  ResolveInviteTokenQueryResult,
  ResolveInviteTokenQueryVariables
> = gql`
    query ResolveInviteToken($token: String!) {
        resolveInviteToken(token: $token) {
            isNewUser
            orgName
        }
    }
`;

export interface AcceptInviteMutationVariables {
  input: {
    token: string
    name?: string
    password?: string
  }
}

export interface AcceptInviteMutationResult {
  acceptInvite: {
    accessToken: string
  }
}

export const ACCEPT_INVITE_MUTATION: TypedDocumentNode<
  AcceptInviteMutationResult,
  AcceptInviteMutationVariables
> = gql`
    mutation AcceptInvite($input: AcceptInviteInput!) {
        acceptInvite(input: $input) {
            accessToken
        }
    }
`;
